import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getJson, PRC_PREFIX } from "./client";

/**
 * The facilities (books) the product knows about -- `GET /facilities`, the LIVE Debt capital sources (Manage Capital), CAP1 and CAP2
 * first. A facility added in Manage Capital shows up here, and in every picker that reads this list, without a code change.
 *
 * A facility is not the same as a facility WITH RISK LIMITS: the ABS Scenario Portfolio's "ABS SCENARIO" book exists (capital, scenario
 * destinations) and has no limit. Capital and scenario pickers list every facility; the limit pages (Monitor, Capacity, Alerts, Manage
 * Limits, Snapshot, Attestation) list only those with `hasLimits`, per investor, through `useInvestorScope`.
 */
export interface Facility {
  /** CAP1, CAP2, or the code a new facility was created with. */
  code: string;
  /** How it reads on screen, e.g. "CAP 1 Back Book". */
  name: string;
  /** The spelling the data carries in contract_type ("CAP 1"; any other facility, its own code). */
  contractType: string;
  /** The investor the facility belongs to (its Debt capital source's in Manage Capital; CAP1 / CAP2 default to the external funder). */
  investorCode: string | null;
  /** True when the facility has risk limits (read from the limit tables, not listed in code). */
  hasLimits: boolean;
}

/** Shown until the list loads (and if it cannot): the two books the product has always had. Used by the Scenario pages. */
export const BASELINE_FACILITIES: Facility[] = [
  { code: "CAP1", name: "CAP 1 Back Book", contractType: "CAP 1", investorCode: "WAFRA", hasLimits: true },
  { code: "CAP2", name: "CAP 2 Back Book", contractType: "CAP 2", investorCode: "WAFRA", hasLimits: true },
];

export function useFacilities() {
  return useQuery({
    queryKey: ["prc-facilities"],
    staleTime: 5 * 60_000,
    queryFn: () => getJson<Facility[]>(`${PRC_PREFIX}facilities`),
  });
}

/** What one investor has to show -- `GET /investor-scope`, read from the database. */
export interface InvestorScope {
  investorCode: string;
  /** ALL of the investor's facilities, each with `hasLimits` (Wafra: CAP 1 and CAP 2, with limits; the ABS Scenario Portfolio: ABS SCENARIO, without). */
  facilities: Facility[];
  /** The investor has facilities at all, i.e. an External scope to show (a facility without limits says so when it is picked). */
  hasFacilities: boolean;
  /** One of its facilities has risk limits (what an alert or a new limit needs). */
  hasExternal: boolean;
  /** The investor has internal limits switched on, i.e. an Internal scope to show (none for Wafra until the client sets any up). */
  hasInternal: boolean;
}

export function useInvestorScopes() {
  return useQuery({
    queryKey: ["prc-investor-scope"],
    staleTime: 5 * 60_000,
    queryFn: () => getJson<InvestorScope[]>(`${PRC_PREFIX}investor-scope`),
  });
}

export interface ScopeOfInvestor {
  facilities: Facility[];
  hasFacilities: boolean;
  hasExternal: boolean;
  hasInternal: boolean;
  /** true until the answer has arrived. While it is, both scopes read as available so the controls do not flicker or switch on their own. */
  isLoading: boolean;
}

/** What `investor` ("ALL": every investor's together) has, from the scopes the server listed (`undefined` while they load). */
export function scopeOfInvestor(all: InvestorScope[] | undefined, investor: string): ScopeOfInvestor {
  if (!all) return { facilities: [], hasFacilities: true, hasExternal: true, hasInternal: true, isLoading: true };
  const mine = investor === "ALL" ? all : all.filter((s) => s.investorCode.toUpperCase() === investor.toUpperCase());
  const seen = new Set<string>();
  const facilities = mine.flatMap((s) => s.facilities).filter((f) => !seen.has(f.code) && !!seen.add(f.code));
  return {
    facilities,
    hasFacilities: mine.some((s) => s.hasFacilities),
    hasExternal: mine.some((s) => s.hasExternal),
    hasInternal: mine.some((s) => s.hasInternal),
    isLoading: false,
  };
}

/** What `investor` ("ALL": every investor's together) has: its facilities with risk limits and whether it has an External / Internal scope. */
export function useInvestorScope(investor: string): ScopeOfInvestor {
  const q = useInvestorScopes();
  return useMemo(() => scopeOfInvestor(q.data, investor), [q.data, investor]);
}

/**
 * How a book reads in a table or sentence: facility code -> "CAP 1" (its contract type), and INTERNAL -> "Internal". Built from the
 * facilities the database lists, so a facility whose limits are set up has a label with no change here; a code with no entry reads as
 * itself (`label[code] ?? code`).
 */
export function useBookLabels(): Record<string, string> {
  const q = useFacilities();
  return useMemo(() => {
    const out: Record<string, string> = { INTERNAL: "Internal" };
    for (const f of q.data ?? []) out[f.code] = f.contractType;
    return out;
  }, [q.data]);
}
